import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/admin/lib/api";
import { useOrg } from "@/admin/hooks/use-org";

export interface FormQuestionRef {
  code: string;
  required: boolean;
  /** Shown only when an earlier question has one of these answers. */
  visible_when?: Record<string, string[]>;
  /** Required only when an earlier question has one of these answers. */
  required_when?: Record<string, string[]>;
  /** Its score counts this many times; 1 when left off. */
  weight?: number;
  /** A failed answer fails the whole form. */
  critical?: boolean;
  /** The section it sits in, when the form has sections. */
  section?: string;
}

/** How a form passes: an optional pass mark, and whether a failed critical fails it. */
export interface FormScoring {
  pass_mark?: number;
  /** On unless set to false. */
  critical_fails?: boolean;
}

/** How a submitted form's answers scored. */
export interface FormScore {
  earned: number;
  possible: number;
  percent: number | null;
  pass_mark: number | null;
  /** Codes of critical questions whose answer failed. */
  failed_critical: string[];
  /** Null when nothing can fail the form: no pass mark, nothing critical. */
  outcome: "pass" | "fail" | null;
}

/** An ordered, titled group of questions. */
export interface FormSection {
  key: string;
  title: string;
  description: string;
}

export interface FormTemplate {
  uuid: string;
  key: string;
  name: string;
  description: string;
  questions: FormQuestionRef[];
  sections: FormSection[];
  scoring: FormScoring;
  record_types: string[];
  is_active: boolean;
  version: number;
  /** The version sends use; null until first published. */
  published_version: number | null;
  /** The form has changed since it was last published. */
  has_draft_changes: boolean;
  updated_at: string;
}

export interface FormQuestion {
  uuid: string;
  code_name: string;
  display_name: string;
  description?: string;
  field_type: string;
  required: boolean;
  hint?: string;
  options?: Array<{ value: string; label: string }>;
  configuration_data?: Record<string, unknown>;
  section?: string;
}

/** "in_progress": filled in the app and not yet submitted. */
export type FormRequestStatus = "sent" | "in_progress" | "submitted" | "expired" | "cancelled";

/** A section's state as the server sees it, and what it still needs. */
export interface FormSectionProgress {
  /** Empty for a form without sections. */
  key: string;
  title: string;
  status: "not_started" | "in_progress" | "complete";
  /** Codes of shown questions still needing an answer. */
  missing: string[];
}

export interface FormRequest {
  uuid: string;
  template_key: string;
  template_name: string;
  record_type: string;
  record_uuid: string;
  status: FormRequestStatus;
  /** Who fills it in the app; null when it went out as a link. */
  respondent: { uuid: string; name: string } | null;
  progress: FormSectionProgress[];
  recipient_name: string;
  recipient_email: string;
  sent_at: string;
  expires_at: string | null;
  submitted_at: string | null;
  cancelled_at: string | null;
  questions: FormQuestion[];
  sections: FormSection[];
  answers: Record<string, unknown>;
  /** Set once a scored form is submitted. */
  score: FormScore | null;
}

/** An answer in a response set, with what it means. */
export interface ResponseSetAnswer {
  /** Left off a new answer: the server makes one from the label. */
  value?: string;
  label: string;
  outcome?: "pass" | "fail" | "na";
  score?: number;
  severity?: "yellow" | "red";
  creates_action?: boolean;
  /** The answer stops the work it belongs to. */
  blocks?: boolean;
  requires?: Array<"photo" | "note">;
  allowed_results?: string[];
}

/** A named list of answers several questions share. */
export interface FormResponseSet {
  uuid: string;
  key: string;
  name: string;
  options: ResponseSetAnswer[];
  is_active: boolean;
  updated_at: string;
}

export type FormTemplateWrite = Partial<
  Pick<
    FormTemplate,
    | "name"
    | "description"
    | "questions"
    | "sections"
    | "scoring"
    | "record_types"
    | "is_active"
  >
> & { key: string };

/**
 * Templates in this org; `recordType` narrows to the ones sendable from it.
 * Forms are internal only, so partner sessions never ask.
 */
export function useFormTemplates(
  opts: { recordType?: string; activeOnly?: boolean; publishedOnly?: boolean } = {},
) {
  const { isPartnerAccess } = useOrg();
  const params = {
    ...(opts.recordType ? { record_type: opts.recordType } : {}),
    ...(opts.activeOnly ? { active: "true" } : {}),
    ...(opts.publishedOnly ? { published: "true" } : {}),
  };
  return useQuery({
    queryKey: ["form-templates", params],
    queryFn: async () =>
      (await api.get<FormTemplate[]>("/forms/templates/", { params })).data ?? [],
    enabled: !isPartnerAccess,
  });
}

export function useSaveFormTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ isNew, ...payload }: FormTemplateWrite & { isNew: boolean }) =>
      isNew
        ? (await api.post<FormTemplate>("/forms/templates/", payload)).data
        : (await api.patch<FormTemplate>(`/forms/templates/${payload.key}/`, payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["form-templates"] }),
  });
}

/** A kind of record a form can be sent from, named in the tenant's own words. */
export interface FormSendFrom {
  value: string;
  label: string;
}

/** The records this tenant can send forms from; inspections only with that module. */
export function useFormSendFrom() {
  const { isPartnerAccess } = useOrg();
  return useQuery({
    queryKey: ["form-templates", "send-from"],
    queryFn: async () => (await api.get<FormSendFrom[]>("/forms/send-from/")).data ?? [],
    enabled: !isPartnerAccess,
  });
}

/** A frozen, published version of a form. */
export interface FormTemplateVersion {
  number: number;
  name: string;
  question_count: number;
  published_at: string;
  /** Empty when published by the platform (a seed). */
  published_by: string;
}

/** A form's published versions, newest first. Under "form-templates", so saves refresh it. */
export function useFormTemplateVersions(key: string | undefined) {
  return useQuery({
    queryKey: ["form-templates", key, "versions"],
    queryFn: async () =>
      (await api.get<FormTemplateVersion[]>(`/forms/templates/${key}/versions/`)).data ?? [],
    enabled: Boolean(key),
  });
}

/** Freeze a form as its next version; sends use it from then on. */
export function usePublishFormTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (key: string) =>
      (await api.post<FormTemplate>(`/forms/templates/${key}/publish/`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["form-templates"] }),
  });
}

/** The tenant's response sets. Forms are internal only, so partner sessions never ask. */
export function useFormResponseSets() {
  const { isPartnerAccess } = useOrg();
  return useQuery({
    queryKey: ["form-response-sets"],
    queryFn: async () =>
      (await api.get<FormResponseSet[]>("/forms/response-sets/")).data ?? [],
    enabled: !isPartnerAccess,
  });
}

export function useSaveFormResponseSet() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      key,
      ...payload
    }: Partial<Pick<FormResponseSet, "name" | "options" | "is_active">> & {
      key?: string;
    }) =>
      key
        ? (await api.patch<FormResponseSet>(`/forms/response-sets/${key}/`, payload)).data
        : (await api.post<FormResponseSet>("/forms/response-sets/", payload)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["form-response-sets"] });
      // Questions on a set show its answers.
      qc.invalidateQueries({ queryKey: ["form-requests"] });
    },
  });
}

/** Forms sent about one record, with their answers. */
export function useRecordForms(recordType: string, recordUuid: string | undefined) {
  const { isPartnerAccess } = useOrg();
  return useQuery({
    queryKey: ["form-requests", recordType, recordUuid],
    queryFn: async () =>
      (
        await api.get<FormRequest[]>("/forms/requests/", {
          params: { record_type: recordType, record_uuid: recordUuid },
        })
      ).data ?? [],
    enabled: !!recordUuid && !isPartnerAccess,
  });
}

function useFormRequestMutation<V>(fn: (vars: V) => Promise<unknown>) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["form-requests"] });
      qc.invalidateQueries({ queryKey: ["communications-activity"] });
    },
  });
}

export function useSendForm() {
  return useFormRequestMutation(
    async (payload: {
      template_key: string;
      record_type: string;
      record_uuid: string;
      recipient_email: string;
      recipient_name?: string;
    }) => (await api.post<FormRequest>("/forms/requests/", payload)).data,
  );
}

export function useResendForm() {
  return useFormRequestMutation(
    async (uuid: string) =>
      (await api.post<FormRequest>(`/forms/requests/${uuid}/resend/`)).data,
  );
}

export function useCancelForm() {
  return useFormRequestMutation(
    async (uuid: string) =>
      (await api.post<FormRequest>(`/forms/requests/${uuid}/cancel/`)).data,
  );
}
